import { BaseDialog } from "./BaseDialog";
import { Box, Typography } from "@mui/material";
import { useMount } from "@app/hooks/useMount";
import { JobStatus } from "@app/pages/assetAllocation/editPortfolioGroup/JobStatus";
import { useEffect, useState } from "react";
import { CarTimer } from "./Icons";

const msecToStr = (ms: number) => {
  const seconds = Math.floor(ms / 1000) % 60;
  const minutes = Math.floor(ms / (1000 * 60)) % 60;
  const hours = Math.floor(ms / (1000 * 60 * 60)) % 24;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));

  const timeString = [];

  if (days > 0) {
    timeString.push(`${days}d`);
  }
  if (hours > 0) {
    timeString.push(`${hours}h`);
  }
  if (minutes > 0) {
    timeString.push(`${minutes}m`);
  }

  timeString.push(`${seconds}s`);

  return timeString.join(" ");
};

export interface InProgressDialogProps {
  onApply: (value: boolean) => void;
  onCancel: () => void;
  message: string;
  lastRunTime?: number;
  run: () => Promise<unknown>;
}

export const InProgressDialog = (props: InProgressDialogProps) => {
  const [startTime] = useState(new Date().valueOf());
  const [runTime, setRunTime] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setRunTime(new Date().valueOf() - startTime);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [startTime]);

  useMount(() => {
    props
      .run()
      .then(() => props.onCancel())
      .catch((e) => {
        console.error("InProgressDialog", e);
        props.onCancel();
      });
  });

  const timeSpent = `Time spent: ${msecToStr(runTime)}`;
  const timeLeft =
    props.lastRunTime && props.lastRunTime - runTime > 0
      ? `Time left (based on last run): ${msecToStr(props.lastRunTime - runTime)}`
      : "";

  return (
    <BaseDialog onClose={props.onCancel} hideCloseButton>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2.5,
        }}
      >
        <JobStatus
          sx={{
            width: 100,
            height: 100,
            "circle.percent": {
              stroke: "rgba(241, 150, 73, 1)",
            },
          }}
          completedCount={0}
          failedCount={0}
          jobCount={1}
          hideLabel
        />
        <Typography
          sx={{
            whiteSpace: "pre-line",
            textAlign: "center",
            lineHeight: "31px",
          }}
          variant="par03Regular"
        >
          {props.message}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CarTimer sx={{ color: "primary.main" }} />
          <Typography variant="par01Regular" sx={{ mt: 0.5 }}>
            {timeSpent}
          </Typography>
        </Box>
        {timeLeft && (
          <Typography variant="mediumItalic" sx={{ mt: 0, color: "gray6" }}>
            {timeLeft}
          </Typography>
        )}
      </Box>
    </BaseDialog>
  );
};
